import React, { useMemo } from 'react'
import { VegaLite } from 'react-vega'
import _cloneDeep from 'lodash/fp/cloneDeep'

import { makeStyles, useTheme, alpha } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Button from '@material-ui/core/Button'
import { Alert, AlertTitle } from '@material-ui/lab'

import {
  useMonitorStreamsQuery,
  useReportQuery,
  useDownloadCsv,
} from '../../services/stream-manager'

import formatDuration from '../../utils/formatDuration'
import formatAPIParams from '../../utils/formatAPIParams'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
    height: '100%'
  },
  grid: {
  },
  title: {
    padding: theme.spacing(1),
  },
  tableContainer: {
    padding: theme.spacing(1),
  },
  chart: {
    width: '100%',
  },
  table: {
    '& tr:last-child th, & tr:last-child td': {
      borderBottom: 'none',
    },
  },
  noData: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    fontSize: theme.typography.subtitle1.fontSize,
    textTransform: 'uppercase',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}))

const StreamHealth = ({ from, until }) => {
  const theme = useTheme()
  const classes = useStyles()

  const monitorStreamsQuery = useMonitorStreamsQuery({
    params: formatAPIParams({ from, until }),
  })

  const [downloadStreamsHealthReport] = useDownloadCsv(useReportQuery)({
    params: formatAPIParams({
      from,
      until,
      topics: ['stream_on', 'stream_off'],
    }),
  })

  const spec = useMemo(() => {
    if (monitorStreamsQuery.data == null) return
    const spec = _cloneDeep(monitorStreamsQuery.data.graph_spec)
    spec.background = theme.palette.background.paper
    spec.config.view.stroke = theme.palette.divider
    spec.width = 'container'
    spec.height = 300
    spec.padding = 20
    spec.autosize = {
      type: 'fit',
      contains: 'padding',
    }
    spec.encoding.color.legend = {
      orient: 'none',
      direction: 'horizontal',
      legendX: 10,
      legendY: -15,
      title: null,
      labelColor: theme.palette.text.primary,
    }
    spec.encoding.color.scale.range = [
      theme.palette.error.main,
      theme.palette.success.main,
    ]
    spec.encoding.y.axis.tickMinStep = 1
    spec.encoding.y.axis.domainColor = alpha(theme.palette.divider, 0.35)
    spec.encoding.y.axis.titleColor = theme.palette.text.secondary
    spec.encoding.y.axis.labelColor = theme.palette.text.primary
    spec.encoding.y.axis.gridColor = theme.palette.divider
    spec.encoding.y.axis.tickColor = theme.palette.divider
    spec.encoding.x.axis.domainColor = alpha(theme.palette.divider, 0.35)
    spec.encoding.x.axis.titleColor = theme.palette.text.secondary
    spec.encoding.x.axis.labelColor = theme.palette.text.primary
    spec.encoding.x.axis.gridColor = theme.palette.divider
    spec.encoding.x.axis.tickColor = theme.palette.divider
    spec.data.format = {
      parse: { d: "utc:'%Y-%m-%dT%H:%M:%S'" },
    }
    return spec
  }, [theme, monitorStreamsQuery.data])

  const longestCameraOff = useMemo(() =>
    formatDuration(monitorStreamsQuery.data?.longest_rtsp_stream_off_secs)
    , [monitorStreamsQuery.data])

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" component="p" className={classes.title}>
        Stream Health
      </Typography>
      {monitorStreamsQuery.isLoading ? (
        <CircularProgress />
      ) : monitorStreamsQuery.isSuccess ? (
        <Grid container spacing={0} className={classes.grid}>
          {spec.datasets[spec.data.name]?.length > 0 ? (
            <>
              <Grid item xs={12}>
                <VegaLite className={classes.chart} spec={spec} actions={false} />
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Box} className={classes.tableContainer}>
                  <Table className={classes.table} size="small" aria-label="stream health info">
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Cameras total
                        </TableCell>
                        <TableCell align="right">{monitorStreamsQuery.data.rtsp_streams}</TableCell>
                        <TableCell component="th" scope="row">
                          Videos total
                        </TableCell>
                        <TableCell align="right">{monitorStreamsQuery.data.video_streams}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Cameras off
                        </TableCell>
                        <TableCell align="right">{monitorStreamsQuery.data.rtsp_streams_off}</TableCell>
                        <TableCell component="th" scope="row">
                          Videos off
                        </TableCell>
                        <TableCell align="right">{monitorStreamsQuery.data.video_streams_off}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Longest stream off time
                        </TableCell>
                        <TableCell align="right">{longestCameraOff}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={downloadStreamsHealthReport}
                >
                  Download CSV report
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="h6" component="p" className={classes.noData}>
                No data recorded for the selected period
              </Typography>
            </Grid>
          )}
        </Grid>
      ) : monitorStreamsQuery.isError ? (
        <Alert severity="error">
          <AlertTitle>{monitorStreamsQuery.error.data?.status_code}</AlertTitle>
          {monitorStreamsQuery.error.data?.detail}
        </Alert>
      ) : null}
    </Paper>
  )
}

export default StreamHealth
